<template>
  <div class="space-y-4">
    <p v-if="rejudgeOf" class="text-gray-600">This run graded the transcripts of #{{ rejudgeOf }} again. Their scores, with every judgment, are shown here.</p>
    <p v-if="error" class="text-red-600">{{ error }}</p>
    <template v-if="view">
      <div class="grid grid-cols-4 gap-4">
        <div v-for="v in variantSummary" :key="v.name" class="rounded-md border border-gray-200 bg-white p-4">
          <span class="block text-gray-500">{{ v.name }} · {{ v.n }} scored</span>
          <span class="text-lg text-gray-900 tabular-nums">{{ percent(v.mean) }}</span>
        </div>
        <div class="rounded-md border border-gray-200 bg-white p-4" :title="judgeTitle">
          <span class="block text-gray-500">Judge agreement</span>
          <template v-if="judge.results">
            <span class="text-lg text-gray-900 tabular-nums">{{ percent(judge.criterion_agreement) }}</span>
            <span class="block text-gray-500 tabular-nums">κ {{ judge.fleiss_kappa ?? '—' }} · score ±{{ points(judge.score_sd) }}</span>
          </template>
          <span v-else class="block text-gray-500">needs two judgments per transcript</span>
        </div>
        <div class="rounded-md border border-gray-200 bg-white p-4" :title="agentTitle">
          <span class="block text-gray-500">Agent run-to-run spread</span>
          <template v-if="agent.groups">
            <span class="text-lg text-gray-900 tabular-nums">±{{ points(agent.agent_sd) }}</span>
            <span class="block text-gray-500 tabular-nums">observed ±{{ points(agent.observed_sd) }} · ICC {{ agent.icc ?? '—' }}</span>
          </template>
          <span v-else class="block text-gray-500">needs two runs per case</span>
        </div>
        <div class="rounded-md border border-gray-200 bg-white p-4">
          <span class="block text-gray-500">Cost · time</span>
          <span class="text-lg text-gray-900 tabular-nums">{{ money((run.usage || {}).cost_usd) }}</span>
          <span class="block text-gray-500 tabular-nums">{{ duration(run.started_at, run.finished_at) }}</span>
        </div>
      </div>
      <p v-if="judge.unsupported_rate" class="text-gray-500">{{ percent(judge.unsupported_rate) }} of the judge's verdicts quoted nothing that is in the transcript; those were not counted for or against the agent.</p>

      <div v-if="view.comparison" class="space-y-2">
        <span class="block font-medium text-gray-900">{{ view.comparison.b }} against {{ view.comparison.a }}</span>
        <CompareTable :comparison="view.comparison" :a-name="view.comparison.a" :b-name="view.comparison.b" />
      </div>

      <details v-if="view.power" class="rounded-md border border-gray-200 bg-white p-4">
        <summary class="cursor-pointer text-gray-700">How big a difference this case set can detect</summary>
        <p class="mt-2 text-gray-500">From this run's spread: the smallest difference in mean score between two variants that a run would detect four times out of five, at p &lt; 0.05, over {{ caseCount }} cases.</p>
        <table class="mt-2">
          <thead><tr><th :class="TH">Runs per case</th><th :class="TH">Judgments per run</th><th :class="[TH, 'text-right']">Detectable</th><th :class="[TH, 'text-right']">Agent sessions per variant</th></tr></thead>
          <tbody><tr v-for="p in view.power" :key="`${p.repeats}-${p.judge_repeats}`">
            <td :class="TD">{{ p.repeats }}</td><td :class="TD">{{ p.judge_repeats }}</td>
            <td :class="[TD, 'text-right tabular-nums']">{{ points(p.detectable_difference) }} pts</td><td :class="[TD, 'text-right tabular-nums']">{{ p.agent_runs_per_variant }}</td>
          </tr></tbody>
        </table>
      </details>

      <div class="rounded-md border border-gray-200 overflow-hidden bg-white">
        <table class="w-full table-fixed">
          <colgroup><col style="width:28%"><col style="width:11%"><col style="width:9%"><col style="width:17%"><col style="width:7%"><col style="width:7%"><col style="width:8%"><col style="width:7%"><col style="width:6%"></colgroup>
          <thead class="bg-gray-50"><tr>
            <th :class="TH">Case</th><th :class="TH">Variant</th><th :class="[TH, 'text-right']">Score</th><th :class="TH">Criteria</th>
            <th :class="[TH, 'text-right']">Turns</th><th :class="[TH, 'text-right']">Calls</th><th :class="[TH, 'text-right']">Time</th><th :class="[TH, 'text-right']">Cost</th><th :class="TH" />
          </tr></thead>
          <tbody class="divide-y divide-gray-200">
            <tr v-if="!lines.length"><td colspan="9" class="px-3 py-8 text-center text-gray-500">{{ run.status === 'failed' ? 'The run failed before any case ran.' : 'Waiting for the first case…' }}</td></tr>
            <template v-for="line in lines" :key="line.key">
              <tr :class="line.row ? 'cursor-pointer hover:bg-neutral-50' : ''" @click="line.row && toggle(line.row.result_id)">
                <td :class="TD">
                  <span class="font-medium">{{ line.label }}</span>
                  <span v-if="line.rep > 1 || repeated" class="text-gray-500"> · run {{ line.rep }}</span>
                  <span v-if="line.error" class="block text-red-600">{{ line.error }}</span>
                  <span v-if="line.row && line.row.stale_truth" class="block text-amber-700">graded against an earlier right answer; rejudge to update</span>
                </td>
                <td :class="TD" class="text-gray-700">{{ line.variant }}</td>
                <td :class="[TD, 'text-right tabular-nums']">
                  <HermesBadge v-if="!line.row" :value="line.status" />
                  <template v-else>{{ line.row.score !== null ? percent(line.row.score) : (line.row.legacy_score !== null && line.row.legacy_score !== undefined ? `${line.row.legacy_score} / 5` : '—') }}</template>
                  <span v-if="line.row && line.row.judgments > 1" class="block text-gray-500">±{{ points(line.row.score_sd) }} · {{ line.row.judgments }}×</span>
                </td>
                <td :class="TD">
                  <span v-if="line.row" class="inline-flex flex-wrap gap-1">
                    <span v-for="c in line.row.criteria" :key="c.id" :title="`${c.id}: ${c.majority}, ${Math.round(c.agreement * 100)}% of judgments agree`" :class="['inline-block h-3 w-3 rounded-sm', DOT[c.majority], c.agreement < 1 ? 'opacity-60 ring-1 ring-gray-400' : '']" />
                  </span>
                  <span v-if="line.row && line.row.labelled" class="ml-1 text-gray-500">· labelled</span>
                </td>
                <td :class="[TD, 'text-right tabular-nums']">{{ line.row ? line.row.user_turns ?? '—' : '—' }}</td>
                <td :class="[TD, 'text-right tabular-nums']">{{ line.row ? line.row.tool_calls ?? '—' : '—' }}</td>
                <td :class="[TD, 'text-right tabular-nums']">{{ line.row && line.row.seconds != null ? seconds(line.row.seconds) : '—' }}</td>
                <td :class="[TD, 'text-right tabular-nums']">{{ money(line.cost) }}</td>
                <td :class="[TD, 'text-right']" @click.stop>
                  <router-link v-if="line.session" :to="{ name: 'Agent', params: { sessionId: line.session } }" target="_blank" :class="TEXT_BUTTON">Session</router-link>
                </td>
              </tr>
              <tr v-if="line.row && open === line.row.result_id">
                <td colspan="9" class="px-4 py-4 bg-neutral-50"><TestResultDetail :result-id="line.row.result_id" @labelled="load" /></td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>
    </template>
  </div>
</template>

<script>
import HermesBadge from '../HermesBadge.vue';
import { API, TD, TEXT_BUTTON, TH, duration, errorOf, money, seconds } from '../hermes';
import { percent } from './bench';
import CompareTable from './CompareTable.vue';
import TestResultDetail from './TestResultDetail.vue';

const DOT = { met: 'bg-green-600', partly: 'bg-amber-400', not_met: 'bg-red-600' };

export default {
  name: 'TestRunResults',
  components: { HermesBadge, CompareTable, TestResultDetail },
  props: { bench: { type: Object, required: true }, run: { type: Object, required: true } },
  data() {
    return { view: null, open: null, error: '', timer: null, DOT, TD, TEXT_BUTTON, TH };
  },
  computed: {
    rejudgeOf() {
      return (this.run.params || {}).rejudge || null;
    },
    judge() {
      return (this.view && this.view.judge_consistency) || {};
    },
    agent() {
      return (this.view && this.view.agent_consistency) || {};
    },
    repeated() {
      return Number((this.run.params || {}).repeats || 1) > 1;
    },
    caseCount() {
      return new Set((this.view ? this.view.rows : []).map((r) => r.case)).size;
    },
    variantSummary() {
      const by = {};
      (this.view ? this.view.rows : []).forEach((r) => {
        if (typeof r.score !== 'number') return;
        (by[r.variant] = by[r.variant] || []).push(r.score);
      });
      const names = (this.view && this.view.variants) || [];
      return names.slice(0, 2).map((name) => {
        const s = by[name] || [];
        return { name, n: s.length, mean: s.length ? s.reduce((a, x) => a + x, 0) / s.length : null };
      });
    },
    lines() {
      if (!this.view) return [];
      const byChild = Object.fromEntries(this.view.rows.map((r) => [r.child_id, r]));
      if (this.rejudgeOf) {
        return this.view.rows.map((r) => ({ key: r.result_id, label: r.label, variant: r.variant, rep: r.rep, row: r, session: r.session_id, cost: r.cost_usd }));
      }
      return this.view.children.map((c) => {
        const m = c.metadata || {};
        const row = byChild[c.id] || null;
        return { key: c.id, label: m.label || c.unit_key, variant: m.variant || 'deployed', rep: m.rep || 1, row, status: c.status,
          error: c.error, session: m.session_id || (row && row.session_id), cost: (c.usage || {}).cost_usd };
      });
    },
    judgeTitle() {
      return 'How often repeated judgments of the same transcript give the same verdict per criterion (κ corrects for chance), and the spread of the score across judgments.';
    },
    agentTitle() {
      return 'Spread of a case\'s score between separate runs of the same variant, with the judge\'s own spread taken out. ICC is the share of score variation that is between cases rather than run to run.';
    },
  },
  mounted() {
    this.load();
  },
  beforeUnmount() {
    clearTimeout(this.timer);
  },
  methods: {
    duration, money, percent, seconds,
    points(x) {
      return typeof x === 'number' ? Math.round(x * 1000) / 10 : '—';
    },
    toggle(id) {
      this.open = this.open === id ? null : id;
    },
    async load() {
      clearTimeout(this.timer);
      try {
        const { data } = await this.$axios.get(`${API}/console/bench/runs/${this.rejudgeOf || this.run.id}/`);
        this.view = data;
        if (['pending', 'running'].includes(this.run.status) || data.children.some((c) => ['pending', 'running'].includes(c.status))) {
          this.timer = setTimeout(this.load, 5000);
        }
      } catch (e) {
        this.error = errorOf(e, 'The run could not be loaded.');
      }
    },
  },
};
</script>
